import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { Outlet, Link } from '@/lib/router-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProcessFormModal } from '../components/ProcessFormModal';
import { ClientFormModal } from '../components/ClientFormModal';
import { ArchiveProcessModal } from '../components/ArchiveProcessModal';
import { TagBadges } from '../components/TagBadge';
import { CreateProcessDto, ProcessDto, TagDto, ClientDto, UpdateClientDto, PaginatedProcessesResponse, MemberDto, UpdateProcessDto, TaskDto } from '@prometeus/contracts';
import { ClientContactsModal } from '../components/ClientContactsModal';
import { ProcessHistoryModal } from '../components/ProcessHistoryModal';
import { TaskFormModal } from '../components/TaskFormModal';
import { ProcessReportModal } from '../components/ProcessReportModal';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useSidebarStore } from '../stores/sidebar.store';
import { exportRowsToXlsx } from '../lib/excel-export';
import { formatCPF, formatCNPJ, formatCEP } from '../lib/validations';

export const Route = createFileRoute('/w/$workspaceId/clients/$clientId')({
  component: ClientDetailLayout,
});

function ClientDetailLayout() {
  const { workspaceId, clientId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);

  // Verificar se estamos na rota de um processo específico (tem /processes/$processId)
  const isProcessDetailRoute = /\/processes\/[^/]+/.test(location.pathname);
  // Verificar se estamos na rota de documentos do cliente
  const isDocumentsRoute = location.pathname.endsWith('/documents') || location.pathname.includes('/documents/');
  const shouldRenderOutletOnly = isProcessDetailRoute || isDocumentsRoute;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState<'caso' | 'processo'>('processo');
  const [createBtnDropdownOpen, setCreateBtnDropdownOpen] = useState(false);
  const createBtnDropdownRef = useRef<HTMLDivElement>(null);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<'all' | 'caso' | 'processo'>('all');
  const [isEditingClientTags, setIsEditingClientTags] = useState(false);
  const [selectedClientTagIds, setSelectedClientTagIds] = useState<string[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingProcessId, setEditingProcessId] = useState<string | null>(null);
  const [archivingProcessId, setArchivingProcessId] = useState<string | null>(null);
  const [showHistoryModalProcessId, setShowHistoryModalProcessId] = useState<string | null>(null);
  const [showTaskModalProcessId, setShowTaskModalProcessId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [processFilterDropdownOpen, setProcessFilterDropdownOpen] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerDropdownTriggerRef = useRef<HTMLButtonElement>(null);
  const [headerDropdownPosition, setHeaderDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const processFilterDropdownRef = useRef<HTMLDivElement>(null);

  const HEADER_DROPDOWN_WIDTH = 192;

  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const canRead = hasPermission('clients.read');
  const canReadContacts = hasPermission('clients.contacts.read');
  const canManageContacts = hasPermission('clients.contacts.manage');
  const canReadProcesses = hasPermission('processes.read');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!isLoadingPermissions && !canRead) {
      showPermissionDenied('Ver cliente');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, isLoadingPermissions, canRead, showPermissionDenied, navigate, workspaceId]);

  const { data: client, isLoading: isLoadingClient, error: clientError } = useQuery<ClientDto>({
    queryKey: ['clients', workspaceId, clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}`);
      return response.data.data;
    },
    enabled: !!clientId && (isLoadingPermissions || canRead) && !shouldRenderOutletOnly,
  });

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(searchInput);
    }, 500); // 500ms delay for smoother search

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Reset to page 1 when search or tag filter changes
  useEffect(() => {
    setPage(1);
  }, [search, selectedTagFilter.join(',')]);

  const { data: processesData, isLoading: isLoadingProcesses } = useQuery<PaginatedProcessesResponse>({
    queryKey: ['processes', workspaceId, clientId, includeArchived, search, page, limit],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (includeArchived) {
          params.append('includeArchived', 'true');
        }
        if (search.trim()) {
          params.append('search', search.trim());
        }
        const queryString = params.toString();
        const url = `/clients/${clientId}/processes?${queryString}`;
        const response = await api.get(url);
        return response.data.data;
      } catch (error) {
        console.error('Erro ao carregar processos:', error);
        return {
          data: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        };
      }
    },
    enabled:
      !!clientId && (isLoadingPermissions || canRead) && canReadProcesses && !shouldRenderOutletOnly,
  });

  const processes = processesData?.data || [];
  const totalPages = processesData?.totalPages || 0;
  const total = processesData?.total || 0;

  const { data: allTags } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const response = await api.get('/tags');
      return response.data.data || [];
    },
    enabled: !!workspaceId && !shouldRenderOutletOnly,
  });

  // Fetch all members for the workspace
  const { data: allMembers } = useQuery<MemberDto[]>({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get('/members');
        return response.data.data || [];
      } catch {
        return [];
      }
    },
    enabled: !!workspaceId && !shouldRenderOutletOnly,
  });

  // Inicializar selectedClientTagIds quando client carregar
  useEffect(() => {
    if (client?.tags && !isEditingClientTags) {
      setSelectedClientTagIds(client.tags.map((tag) => tag.id));
    }
  }, [client?.tags, isEditingClientTags]);

  const updateClientTagsMutation = useMutation({
    mutationFn: async (tagIds: string[]) => {
      const updateDto: UpdateClientDto = { tagIds };
      const response = await api.put(`/clients/${clientId}`, updateDto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId, clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      setIsEditingClientTags(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar tags do cliente:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar tags');
    },
  });

  const handleSaveClientTags = () => {
    updateClientTagsMutation.mutate(selectedClientTagIds);
  };

  const handleCancelEditClientTags = () => {
    if (client?.tags) {
      setSelectedClientTagIds(client.tags.map((tag) => tag.id));
    }
    setIsEditingClientTags(false);
  };

  const toggleClientTag = (tagId: string) => {
    setSelectedClientTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  // Filtrar processos por tags selecionadas (processo deve ter pelo menos uma das tags)
  const filteredProcesses = useMemo(() => {
    let result = processes || [];
    if (selectedTagFilter.length) {
      result = result.filter((process) =>
        process.tags?.some((tag) => selectedTagFilter.includes(tag.id))
      );
    }
    if (typeFilter !== 'all') {
      result = result.filter((process) => (process as any).type === typeFilter);
    }
    return result;
  }, [processes, selectedTagFilter, typeFilter]);

  // Fechar dropdown de filtro de processos ao clicar fora (mas não ao clicar dentro)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Se clicou dentro do container do filtro ou dentro do próprio dropdown, não fecha
      if (
        processFilterDropdownRef.current &&
        (processFilterDropdownRef.current.contains(target) ||
          !!target.closest('[data-process-filter-dropdown]'))
      ) {
        return;
      }

      setProcessFilterDropdownOpen(false);
    };

    if (processFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [processFilterDropdownOpen]);

  // Fechar dropdown do botão Criar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createBtnDropdownRef.current && !createBtnDropdownRef.current.contains(event.target as Node)) {
        setCreateBtnDropdownOpen(false);
      }
    };
    if (createBtnDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [createBtnDropdownOpen]);

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead) && !shouldRenderOutletOnly,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateProcessDto) => {
      const response = await api.post(`/clients/${clientId}/processes`, data);
      return response.data.data;
    },
    onSuccess: (newProcess) => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setShowCreateModal(false);
      // Navegar para o novo processo (client-side)
      navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${newProcess.id}/` });
    },
    onError: (error: any) => {
      console.error('Erro ao criar processo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar processo';
      alert(errorMessage);
    },
  });

  const updateProcessMutation = useMutation({
    mutationFn: async ({ processId, data }: { processId: string; data: UpdateProcessDto }) => {
      const response = await api.put(`/clients/${clientId}/processes/${processId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      setEditingProcessId(null);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar processo:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar processo');
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: async (data: UpdateClientDto) => {
      const response = await api.put(`/clients/${clientId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId, clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      setShowEditClientModal(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar cliente:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar cliente');
    },
  });

  const handleSaveClient = (data: UpdateClientDto) => {
    updateClientMutation.mutate(data);
  };

  const deleteClientMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/clients/${clientId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    },
    onError: (error: any) => {
      console.error('Erro ao excluir cliente:', error);
      alert(error.response?.data?.message || 'Erro ao excluir cliente');
    },
  });

  const handleDeleteClient = () => {
    if (!requirePermission('clients.delete', 'Excluir cliente')) return;
    if (confirm('Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.')) {
      deleteClientMutation.mutate();
    }
  };

  const archiveClientMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/clients/${clientId}/archive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId, clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
    },
    onError: (error: any) => {
      console.error('Erro ao reativar cliente:', error);
      alert(error.response?.data?.message || 'Erro ao reativar cliente');
    },
  });

  const handleReactivateClient = () => {
    if (!requirePermission('clients.archive', 'Reativar cliente')) return;
    archiveClientMutation.mutate();
  };

  const formatDateExport = (d: Date | string | null | undefined): string => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleExportProcessList = async () => {
    if (!clientId) return;
    setIsExporting(true);
    try {
      const allProcesses: ProcessDto[] = [];
      let page = 1;
      const limit = 1000;
      let totalToFetch = 1;
      do {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (includeArchived) params.append('includeArchived', 'true');
        const res = await api.get(`/clients/${clientId}/processes?${params.toString()}`);
        const result = res.data?.data ?? res.data;
        const data = Array.isArray(result) ? result : (result?.data ?? []);
        const total = Array.isArray(result) ? data.length : (result?.total ?? data.length);
        const totalPages = Array.isArray(result) ? 1 : (result?.totalPages ?? 1);
        if (page === 1) totalToFetch = total;
        allProcesses.push(...(data as ProcessDto[]));
        if (allProcesses.length >= totalToFetch || page >= totalPages) break;
        page++;
      } while (true);

      if (allProcesses.length === 0) {
        alert('Nenhum processo para exportar.');
        setIsExporting(false);
        return;
      }

      const excelData = allProcesses.map((p) => {
        const partsText = (p.parts ?? [])
          .map((part) => `${part.name} (${part.pole})${part.document ? ` - ${part.document}` : ''}`)
          .join(' | ');
        const tagsText = (p.tags ?? []).map((t) => t.name).join(', ');
        const responsibleName = (p as any).responsibleMember?.user?.name ?? (p as any).responsibleMember?.name ?? '';
        return {
          'Número do processo': p.processNumber ?? '',
          'Tribunal': p.court ?? '',
          'Status': p.status ?? '',
          'Polo do cliente': p.clientPole ?? '',
          'Valor da causa': p.caseValue ?? '',
          'Data distribuição': formatDateExport(p.distributionDate),
          'Número CNJ': p.cnjNumber ?? '',
          'Órgão': p.courtOrg ?? '',
          'Comarca': p.jurisdiction ?? '',
          'Vara/sala': p.courtRoom ?? '',
          'Classe judicial': p.judicialClass ?? '',
          'Título interno': p.internalTitle ?? '',
          'Objeto da ação': p.actionObject ?? '',
          'Resumo': p.summary ?? '',
          'Responsável': responsibleName,
          'Tags': tagsText,
          'Data citação': formatDateExport(p.citationDate),
          'Data arquivamento': formatDateExport(p.filingDate),
          'Fase': p.phase ?? '',
          'Substatus': p.substatus ?? '',
          'Prioridade': p.priority ?? '',
          'Justiça secreta': p.secretJustice ? 'Sim' : 'Não',
          'Arquivado em': formatDateExport(p.archivedAt),
          'Arquivado por': (p.archivedBy as any)?.name ?? '',
          'Tipo conclusão': p.conclusionType ?? '',
          'Observações conclusão': p.conclusionNotes ?? '',
          'Valor condenação': p.condemnationValue ?? '',
          'Valor proveito econômico': p.economicBenefitValue ?? '',
          'Valor atualizado causa': p.updatedCaseValue ?? '',
          'Partes (nome, polo, documento)': partsText,
          'Partes (texto legado)': p.parties ?? '',
          'Data criação': formatDateExport(p.createdAt),
          'Data atualização': formatDateExport(p.updatedAt),
        };
      });

      await exportRowsToXlsx({
        sheetName: 'Processos',
        fileName: `processos-${client?.name ?? clientId}-${new Date().toISOString().split('T')[0]}.xlsx`,
        rows: excelData,
      });
    } catch (err: any) {
      console.error('Erro ao exportar processos:', err);
      alert(err.response?.data?.message || err.message || 'Erro ao exportar planilha.');
    } finally {
      setIsExporting(false);
    }
  };

  // Posição do dropdown do header (portal)
  useEffect(() => {
    if (!dropdownOpen || !headerDropdownTriggerRef.current) {
      setHeaderDropdownPosition(null);
      return;
    }
    const rect = headerDropdownTriggerRef.current.getBoundingClientRect();
    setHeaderDropdownPosition({
      top: rect.bottom + 8,
      left: rect.right - HEADER_DROPDOWN_WIDTH,
    });
  }, [dropdownOpen]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && !target.closest('[data-client-header-dropdown]')) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Fechar dropdown ao rolar
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleScroll = () => setDropdownOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [dropdownOpen]);

  // Formatar documento
  const formattedDocument = useMemo(() => {
    if (!client?.document) return null;
    return client.type === 'INDIVIDUAL' ? formatCPF(client.document) : formatCNPJ(client.document);
  }, [client?.document, client?.type]);

  // Formatar endereço resumido
  const formattedAddress = useMemo(() => {
    const parts: string[] = [];
    if (client?.city) parts.push(client.city);
    if (client?.state) parts.push(client.state);
    return parts.length > 0 ? parts.join(' - ') : null;
  }, [client?.city, client?.state]);

  // Formatar endereço completo
  const fullAddress = useMemo(() => {
    if (!client) return null;
    const parts: string[] = [];
    if (client.street) parts.push(client.street);
    if (client.number) parts.push(`número ${client.number}`);
    if (client.neighborhood) parts.push(`bairro ${client.neighborhood}`);
    if (client.city) parts.push(`cidade ${client.city}`);
    if (client.state) parts.push(`estado ${client.state}`);
    if (client.zipCode) parts.push(`cep ${formatCEP(client.zipCode)}`);
    
    return parts.length > 0 ? `Rua ${parts.join(', ')}.` : null;
  }, [client]);

  if (shouldRenderOutletOnly) {
    return <Outlet />;
  }

  if (isLoadingClient) {
    return <div className="p-8">Carregando informações do cliente...</div>;
  }

  if (clientError) {
    console.error('Erro ao carregar cliente:', clientError);
    return <div className="p-8">Erro ao carregar informações do cliente</div>;
  }

  if (!client) {
    return <div className="p-8">Cliente não encontrado</div>;
  }

  if (!isLoadingPermissions && !canRead) return null;

  // Determinar situação do cliente
  const clientStatus = client.archivedAt ? 'Arquivado' : 'Ativo';

  // Obter iniciais do nome do cliente
  const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Clientes', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: client.name },
          ]}
        />
        
        {/* Container do cabeçalho do cliente */}
        <div
          className="mt-6 bg-white dark:bg-gray-800 relative"
          style={{
            fontFamily: 'Poppins, sans-serif',
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Layout compacto */}
          <div className="flex items-center justify-between p-4">
            {/* Lado esquerdo: Avatar, Nome e CNPJ/CPF */}
            <div className="flex items-center gap-3">
              {/* Avatar do cliente */}
              <div 
                className="flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{ 
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: '#06141B',
                }}
              >
                {client.avatarUrl ? (
                  <img 
                    src={client.avatarUrl} 
                    alt={client.name}
                    className="w-full h-full object-cover"
                    style={{
                      borderRadius: 10,
                    }}
                  />
                ) : (
                  <span 
                    className="text-[14px] font-normal"
                    style={{ 
                      fontFamily: 'Poppins, sans-serif', 
                      color: '#F5F6F5',
                      textTransform: 'lowercase',
                    }}
                  >
                    {getInitials(client.name).toLowerCase()}
                  </span>
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-[14px] font-normal" style={{ fontFamily: 'Poppins, sans-serif', color: '#06141B' }}>
                    {client.name}
                  </h1>
                  <span
                    className="px-2.5 py-1 rounded-[10px] whitespace-nowrap flex-shrink-0"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: 10,
                      fontWeight: 400,
                      backgroundColor: clientStatus === 'Ativo' ? '#10B98120' : '#71809620',
                      color: clientStatus === 'Ativo' ? '#10B981' : '#718096',
                    }}
                  >
                    {clientStatus}
                  </span>
                </div>
                {formattedDocument && (
                  <span className="text-[12px] font-normal mt-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#2D3748' }}>
                    <span className="font-semibold">CPF/CNPJ: </span>
                    {formattedDocument}
                  </span>
                )}
                <span className="text-[12px] font-normal mt-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#9BA8AB' }}>
                  Modificado em {new Date(client.updatedAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>

            {/* Lado direito: Reativar (se arquivado), Contatos, Gerar Relatório, Expandir e Dropdown */}
            <div className="flex items-center gap-4">
              {/* Botão Reativar - visível apenas quando cliente está arquivado */}
              {client.archivedAt && (
                <button
                  type="button"
                  onClick={handleReactivateClient}
                  disabled={archiveClientMutation.isPending}
                  className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    fontWeight: 500,
                    backgroundColor: '#10B981',
                    color: '#FFFFFF',
                  }}
                >
                  {archiveClientMutation.isPending ? 'Reativando...' : 'Reativar'}
                </button>
              )}
              {/* Botão Contatos - visível na versão minimizada */}
              {canReadContacts && (
                <button
                  type="button"
                  onClick={() => setShowContactsModal(true)}
                  className="px-4 py-2 rounded transition-colors flex items-center gap-2"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    backgroundColor: '#06141B',
                    borderRadius: 10,
                    color: '#F9FAFB'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 18.4167C20.3943 18.4167 23.4812 19.3999 25.7522 20.784C26.8855 21.4782 27.8545 22.2927 28.5543 23.1782C29.2428 24.0508 29.75 25.0935 29.75 26.2084C29.75 27.4055 29.1677 28.349 28.3291 29.0219C27.5357 29.6594 26.4888 30.0816 25.3767 30.3762C23.1412 30.967 20.1577 31.1667 17 31.1667C13.8422 31.1667 10.8587 30.9684 8.62325 30.3762C7.51117 30.0816 6.46425 29.6594 5.67092 29.0219C4.83083 28.3476 4.25 27.4055 4.25 26.2084C4.25 25.0935 4.75717 24.0508 5.44567 23.1782C6.1455 22.2927 7.11308 21.4782 8.24783 20.784C10.5187 19.3999 13.6071 18.4167 17 18.4167Z" fill="#F9FAFB"/>
                    <path opacity="0.3" d="M16.9998 2.83325C22.4526 2.83325 25.8611 8.7365 23.134 13.4583C22.5123 14.535 21.6181 15.4292 20.5414 16.0509C19.4646 16.6726 18.2432 16.9999 16.9998 16.9999C11.5471 16.9999 8.13855 11.0967 10.8656 6.37492C11.4873 5.29814 12.3815 4.40397 13.4582 3.78228C14.535 3.16059 15.7564 2.83328 16.9998 2.83325Z" fill="#F9FAFB"/>
                  </svg>
                  Contatos
                </button>
              )}

              {/* Botão Gerar Relatório - visível na versão minimizada */}
              <button
                type="button"
                onClick={() => setShowReportModal(true)}
                className="px-4 py-2 rounded transition-colors flex items-center gap-2"
                style={{ 
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  backgroundColor: '#06141B',
                  borderRadius: 10,
                  color: '#F9FAFB'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.25 13.5C5.05109 13.5 4.86032 13.421 4.71967 13.2803C4.57902 13.1397 4.5 12.9489 4.5 12.75V9.75C4.5 9.55109 4.57902 9.36032 4.71967 9.21967C4.86032 9.07902 5.05109 9 5.25 9C5.44891 9 5.63968 9.07902 5.78033 9.21967C5.92098 9.36032 6 9.55109 6 9.75V12.75C6 12.9489 5.92098 13.1397 5.78033 13.2803C5.63968 13.421 5.44891 13.5 5.25 13.5ZM9 13.5C8.80109 13.5 8.61032 13.421 8.46967 13.2803C8.32902 13.1397 8.25 12.9489 8.25 12.75V5.25C8.25 5.05109 8.32902 4.86032 8.46967 4.71967C8.61032 4.57902 8.80109 4.5 9 4.5C9.19891 4.5 9.38968 4.57902 9.53033 4.71967C9.67098 4.86032 9.75 5.05109 9.75 5.25V12.75C9.75 12.9489 9.67098 13.1397 9.53033 13.2803C9.38968 13.421 9.19891 13.5 9 13.5ZM12.75 13.5C12.5511 13.5 12.3603 13.421 12.2197 13.2803C12.079 13.1397 12 12.9489 12 12.75V8.25C12 8.05109 12.079 7.86032 12.2197 7.71967C12.3603 7.57902 12.5511 7.5 12.75 7.5C12.9489 7.5 13.1397 7.57902 13.2803 7.71967C13.421 7.86032 13.5 8.05109 13.5 8.25V12.75C13.5 12.9489 13.421 13.1397 13.2803 13.2803C13.1397 13.421 12.9489 13.5 12.75 13.5Z" fill="#F9FAFB"/>
                  <path opacity="0.3" d="M14.25 1.5H3.75C3.15326 1.5 2.58097 1.73705 2.15901 2.15901C1.73705 2.58097 1.5 3.15326 1.5 3.75V14.25C1.5 14.8467 1.73705 15.419 2.15901 15.841C2.58097 16.2629 3.15326 16.5 3.75 16.5H14.25C14.8467 16.5 15.419 16.2629 15.841 15.841C16.2629 15.419 16.5 14.8467 16.5 14.25V3.75C16.5 3.15326 16.2629 2.58097 15.841 2.15901C15.419 1.73705 14.8467 1.5 14.25 1.5ZM6 12.75C6 12.9489 5.92098 13.1397 5.78033 13.2803C5.63968 13.421 5.44891 13.5 5.25 13.5C5.05109 13.5 4.86032 13.421 4.71967 13.2803C4.57902 13.1397 4.5 12.9489 4.5 12.75V9.75C4.5 9.55109 4.57902 9.36032 4.71967 9.21967C4.86032 9.07902 5.05109 9 5.25 9C5.44891 9 5.63968 9.07902 5.78033 9.21967C5.92098 9.36032 6 9.55109 6 9.75V12.75ZM9.75 12.75C9.75 12.9489 9.67098 13.1397 9.53033 13.2803C9.38968 13.421 9.19891 13.5 9 13.5C8.80109 13.5 8.61032 13.421 8.46967 13.2803C8.32902 13.1397 8.25 12.9489 8.25 12.75V5.25C8.25 5.05109 8.32902 4.86032 8.46967 4.71967C8.61032 4.57902 8.80109 4.5 9 4.5C9.19891 4.5 9.38968 4.57902 9.53033 4.71967C9.67098 4.86032 9.75 5.05109 9.75 5.25V12.75ZM13.5 12.75C13.5 12.9489 13.421 13.1397 13.2803 13.2803C13.1397 13.421 12.9489 13.5 12.75 13.5C12.5511 13.5 12.3603 13.421 12.2197 13.2803C12.079 13.1397 12 12.9489 12 12.75V8.25C12 8.05109 12.079 7.86032 12.2197 7.71967C12.3603 7.57902 12.5511 7.5 12.75 7.5C12.9489 7.5 13.1397 7.57902 13.2803 7.71967C13.421 7.86032 13.5 8.05109 13.5 8.25V12.75Z" fill="#F9FAFB"/>
                </svg>
                Gerar Relatório
              </button>

              {/* Botão Documentos - visível na versão minimizada */}
              <button
                type="button"
                onClick={() => navigate({ to: '/w/$workspaceId/clients/$clientId/documents', params: { workspaceId, clientId }, search: { folder: undefined } })}
                className="px-4 py-2 rounded transition-colors flex items-center gap-2"
                style={{ 
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  backgroundColor: '#06141B',
                  borderRadius: 10,
                  color: '#F9FAFB'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path opacity="0.3" d="M28.0783 8.5C28.4897 8.50016 28.8962 8.58991 29.2694 8.76303C29.6426 8.93614 29.9737 9.18847 30.2395 9.50246C30.5054 9.81645 30.6997 10.1846 30.8089 10.5812C30.9181 10.9779 30.9396 11.3936 30.872 11.7994L28.5118 25.9661C28.4015 26.6277 28.06 27.2286 27.5482 27.6621C27.0364 28.0956 26.3874 28.3334 25.7167 28.3333H8.2832C7.6125 28.3334 6.96351 28.0956 6.4517 27.6621C5.93989 27.2286 5.59845 26.6277 5.48812 25.9661L3.12795 11.7994C3.06027 11.3936 3.08179 10.9779 3.191 10.5812C3.30021 10.1846 3.4945 9.81645 3.76036 9.50246C4.02622 9.18847 4.35727 8.93614 4.73051 8.76303C5.10374 8.58991 5.51019 8.50016 5.92162 8.5H28.0783Z" fill="#F9FAFB"/>
                  <path d="M25.5002 4.25C25.8759 4.25 26.2362 4.39926 26.5019 4.66493C26.7676 4.93061 26.9168 5.29094 26.9168 5.66667C26.9168 6.04239 26.7676 6.40272 26.5019 6.6684C26.2362 6.93408 25.8759 7.08333 25.5002 7.08333H8.50016C8.12444 7.08333 7.7641 6.93408 7.49843 6.6684C7.23275 6.40272 7.0835 6.04239 7.0835 5.66667C7.0835 5.29094 7.23275 4.93061 7.49843 4.66493C7.7641 4.39926 8.12444 4.25 8.50016 4.25H25.5002Z" fill="#F9FAFB"/>
                </svg>
                Documentos
              </button>

              {/* Botão de expandir */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 rounded transition-colors"
                style={{ backgroundColor: '#06141B', borderRadius: 10 }}
                aria-label={isExpanded ? 'Recolher' : 'Expandir'}
              >
                <svg 
                  className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="#F9FAFB" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown de opções */}
              <div className="relative" ref={dropdownRef}>
                <button
                  ref={headerDropdownTriggerRef}
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="p-2 hover:bg-gray-50 rounded transition-colors"
                  style={{ color: '#06141B' }}
                  aria-label="Opções"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 20q-.825 0-1.412-.587T10 18t.588-1.412T12 16t1.413.588T14 18t-.587 1.413T12 20m0-6q-.825 0-1.412-.587T10 12t.588-1.412T12 10t1.413.588T14 12t-.587 1.413T12 14m0-6q-.825 0-1.412-.587T10 6t.588-1.412T12 4t1.413.588T14 6t-.587 1.413T12 8" />
                  </svg>
                </button>

                {dropdownOpen && headerDropdownPosition && createPortal(
                  <div
                    data-client-header-dropdown
                    className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]"
                    style={{
                      top: headerDropdownPosition.top,
                      left: headerDropdownPosition.left,
                    }}
                  >
                    <ul className="py-1">
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            if (!requirePermission('clients.update', 'Editar cliente')) return;
                            setShowEditClientModal(true);
                            setDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Editar
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            setCreateModalType('processo');
                            setShowCreateModal(true);
                            setDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11 13v3q0 .425.288.713T12 17t.713-.288T13 16v-3h3q.425 0 .713-.288T17 12t-.288-.712T16 11h-3V8q0-.425-.288-.712T12 7t-.712.288T11 8v3H8q-.425 0-.712.288T7 12t.288.713T8 13zm1 9q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8" />
                          </svg>
                          Criar processo
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            setCreateModalType('caso');
                            setShowCreateModal(true);
                            setDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11 13v3q0 .425.288.713T12 17t.713-.288T13 16v-3h3q.425 0 .713-.288T17 12t-.288-.712T16 11h-3V8q0-.425-.288-.712T12 7t-.712.288T11 8v3H8q-.425 0-.712.288T7 12t.288.713T8 13zm1 9q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8" />
                          </svg>
                          Criar caso
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            handleDeleteClient();
                            setDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Excluir
                        </button>
                      </li>
                    </ul>
                  </div>,
                  document.body
                )}
              </div>
            </div>
          </div>

          {/* Conteúdo expandido */}
          <div 
            className={`overflow-hidden transform transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="mt-4 pt-4 px-4 pb-4">
              <div className="space-y-3">
                {/* Telefone */}
                {client.phone && (
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-medium text-gray-700" style={{ fontFamily: 'Poppins' }}>
                      Telefone:
                    </span>
                    <span className="text-[12px] text-gray-600" style={{ fontFamily: 'Poppins' }}>
                      {client.phone}
                    </span>
                  </div>
                )}

                {/* Email */}
                {client.email && (
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-medium text-gray-700" style={{ fontFamily: 'Poppins' }}>
                      Email:
                    </span>
                    <span className="text-[12px] text-gray-600" style={{ fontFamily: 'Poppins' }}>
                      {client.email}
                    </span>
                  </div>
                )}

                {/* Endereço completo */}
                {fullAddress && (
                  <div className="flex items-start gap-2">
                    <span className="text-[12px] font-medium text-gray-700" style={{ fontFamily: 'Poppins' }}>
                      Endereço completo:
                    </span>
                    <span className="text-[12px] text-gray-600" style={{ fontFamily: 'Poppins' }}>
                      {fullAddress}
                    </span>
                  </div>
                )}

                {/* Tags do cliente */}
                <div className="flex items-start gap-2">
                  <span className="text-[12px] font-medium" style={{ fontFamily: 'Poppins, sans-serif', color: '#2D3748' }}>
                    Tags do cliente:
                  </span>
                  <div className="flex-1">
                    {!isEditingClientTags ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {client.tags && client.tags.length > 0 ? (
                          <>
                            {client.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                                style={{
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: 10,
                                  fontWeight: 400,
                                  backgroundColor: `${tag.color}20`,
                                  color: tag.color,
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                if (!requirePermission('clients.update', 'Editar cliente')) return;
                                if (client?.tags) {
                                  setSelectedClientTagIds(client.tags.map((tag) => tag.id));
                                }
                                setIsEditingClientTags(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:opacity-80"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                backgroundColor: '#06141B',
                                color: '#F9FAFB',
                                borderRadius: 10,
                              }}
                              title="Adicionar tags"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', color: '#9BA8AB' }}>Nenhuma tag atribuída</p>
                            <button
                              type="button"
                              onClick={() => {
                                if (!requirePermission('clients.update', 'Editar cliente')) return;
                                if (client?.tags) {
                                  setSelectedClientTagIds(client.tags.map((tag) => tag.id));
                                }
                                setIsEditingClientTags(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:opacity-80"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                backgroundColor: '#06141B',
                                color: '#F9FAFB',
                                borderRadius: 10,
                              }}
                              title="Adicionar tags"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {allTags && allTags.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {allTags.map((tag) => {
                              const isSelected = selectedClientTagIds.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => toggleClientTag(tag.id)}
                                  className="px-2.5 py-1 rounded-[10px] whitespace-nowrap transition-all inline-flex items-center"
                                  style={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: 10,
                                    fontWeight: 400,
                                    backgroundColor: isSelected ? `${tag.color}20` : `${tag.color}12`,
                                    color: tag.color,
                                    border: isSelected ? `1px solid ${tag.color}` : '1px solid transparent',
                                  }}
                                >
                                  {tag.name}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', color: '#9BA8AB' }}>Nenhuma tag disponível</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button
                            type="button"
                            onClick={handleSaveClientTags}
                            disabled={updateClientTagsMutation.isPending}
                            className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                            style={{
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: '12px',
                              backgroundColor: '#06141B',
                              color: '#F9FAFB',
                              borderRadius: 10,
                            }}
                          >
                            {updateClientTagsMutation.isPending ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditClientTags}
                            disabled={updateClientTagsMutation.isPending}
                            className="px-4 py-2 rounded-lg transition-colors hover:opacity-90"
                            style={{
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: '12px',
                              backgroundColor: '#F9FAFB',
                              color: '#06141B',
                              border: '1px solid #06141B',
                              borderRadius: 10,
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Processos */}
        <div className="mt-6">
          {/* Container de processos com mesmo padrão visual do container de clientes */}
          <div
            className="bg-white dark:bg-gray-800 relative"
            style={{ fontFamily: 'Poppins, sans-serif', borderRadius: 15, boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
          >
          <div className="px-6 pt-4 pb-2">
            <div className="flex justify-between items-start mb-2">
              <div ref={processFilterDropdownRef}>
                <h2
                  className="mb-1"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 400, color: '#06141B' }}
                >
                  Processos e casos
                </h2>
                <p
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#4A5C6A',
                  }}
                >
                  {total} {total === 1 ? 'item' : 'itens'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Search Input - mesmo modelo visual da lista de clientes */}
                <div className="relative w-[250px]">
                  <input
                    type="text"
                    placeholder="Busca por processo"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-0 placeholder-[#CCD0CF]"
                    style={{ 
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      color: '#06141B',
                      backgroundColor: '#F9FAFB',
                      borderRadius: 10,
                      outline: 'none',
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.outline = 'none';
                    }}
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none"
                    fill="none"
                    stroke="#CCD0CF"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {/* Botão Filtro - mesmo padrão e funcionalidade do filtro de clientes */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProcessFilterDropdownOpen((v) => !v)}
                    className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      backgroundColor: '#06141B',
                      fontWeight: 400,
                      borderRadius: 10,
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    }}
                    title="Filtros"
                  >
                    <img src="/filtro.svg" alt="Filtro" className="w-3 h-3 object-contain" />
                  </button>
                  {processFilterDropdownOpen && (
                    <div
                      data-process-filter-dropdown
                      className="absolute right-0 top-full mt-2 z-50 min-w-[200px] py-2 rounded-[10px] border border-gray-200 dark:border-gray-600"
                      style={{
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-xs font-medium" style={{ color: '#06141B', fontSize: 12 }}>Tags</span>
                      </div>
                      {allTags && allTags.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto">
                          {allTags.map((tag) => (
                            <label
                              key={tag.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedTagFilter.includes(tag.id)}
                                onChange={() => {
                                  setSelectedTagFilter((prev) =>
                                    prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
                                  );
                                }}
                                className="rounded border-gray-300"
                              />
                              <span className="text-xs" style={{ color: '#06141B', fontSize: 12 }}>{tag.name}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-xs text-gray-500" style={{ fontSize: 12 }}>Nenhuma tag cadastrada</div>
                      )}
                      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-2 px-3">
                        <span className="text-xs font-medium" style={{ color: '#06141B', fontSize: 12 }}>Tipo</span>
                        {(['all', 'processo', 'caso'] as const).map((opt) => (
                          <label key={opt} className="flex items-center gap-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                            <input
                              type="radio"
                              name="typeFilter"
                              checked={typeFilter === opt}
                              onChange={() => setTypeFilter(opt)}
                              className="border-gray-300"
                            />
                            <span className="text-xs" style={{ color: '#06141B', fontSize: 12 }}>
                              {opt === 'all' ? 'Todos' : opt === 'processo' ? 'Processos' : 'Casos'}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-2 px-3">
                        <label className="flex items-center gap-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-xs" style={{ color: '#06141B', fontSize: 12 }}>Arquivados</span>
                        </label>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-2 px-3">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTagFilter([]);
                            setTypeFilter('all');
                            setIncludeArchived(false);
                            setProcessFilterDropdownOpen(false);
                          }}
                          className="w-full text-left py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors"
                          style={{ color: '#06141B', fontSize: 12 }}
                        >
                          Limpar filtros
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botão Exportar lista - padrão visual do botão Criar Cliente */}
                <button
                  onClick={handleExportProcessList}
                  disabled={isExporting}
                  className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80 flex-shrink-0 disabled:opacity-50"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '18px',
                    backgroundColor: '#06141B',
                    color: '#F5F6F5',
                    fontWeight: 400,
                    borderRadius: 10,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  title={isExporting ? 'Exportando...' : 'Exportar lista de processos em planilha'}
                >
                  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: 'currentColor' }}>
                    <path d="M11.2932 2.793L8.29319 5.793C8.20035 5.88591 8.12671 5.9962 8.07649 6.11757C8.02627 6.23893 8.00044 6.369 8.00049 6.50035C8.00053 6.6317 8.02645 6.76175 8.07676 6.88309C8.12707 7.00442 8.20078 7.11466 8.29369 7.2075C8.3866 7.30035 8.49689 7.37398 8.61826 7.4242C8.73962 7.47442 8.8697 7.50025 9.00104 7.5002C9.13239 7.50016 9.26245 7.47424 9.38378 7.42393C9.50511 7.37362 9.61535 7.29991 9.70819 7.207L11.0002 5.914V11.5C11.0002 11.7652 11.1055 12.0196 11.2931 12.2071C11.4806 12.3946 11.735 12.5 12.0002 12.5C12.2654 12.5 12.5198 12.3946 12.7073 12.2071C12.8948 12.0196 13.0002 11.7652 13.0002 11.5V5.914L14.2932 7.207C14.3854 7.30251 14.4958 7.37869 14.6178 7.4311C14.7398 7.48351 14.871 7.5111 15.0038 7.51225C15.1366 7.5134 15.2682 7.4881 15.3911 7.43782C15.514 7.38754 15.6257 7.31329 15.7196 7.21939C15.8135 7.1255 15.8877 7.01385 15.938 6.89095C15.9883 6.76806 16.0136 6.63638 16.0124 6.5036C16.0113 6.37082 15.9837 6.2396 15.9313 6.1176C15.8789 5.99559 15.8027 5.88525 15.7072 5.793L12.7072 2.793C12.6143 2.70002 12.504 2.62627 12.3826 2.57594C12.2612 2.52562 12.1311 2.49971 11.9997 2.49971C11.8683 2.49971 11.7381 2.52562 11.6168 2.57594C11.4954 2.62627 11.3851 2.70002 11.2922 2.793H11.2932Z" fill="currentColor"/>
                    <path opacity="0.5" d="M13 11.5C13 11.7652 12.8946 12.0196 12.7071 12.2071C12.5196 12.3946 12.2652 12.5 12 12.5C11.7348 12.5 11.4804 12.3946 11.2929 12.2071C11.1054 12.0196 11 11.7652 11 11.5V7.5L5 7.5C4.20459 7.50079 3.44199 7.81712 2.87956 8.37956C2.31712 8.94199 2.00079 9.70459 2 10.5L2 18.5C2.00079 19.2954 2.31712 20.058 2.87956 20.6204C3.44199 21.1829 4.20459 21.4992 5 21.5L19 21.5C19.7954 21.4992 20.558 21.1829 21.1204 20.6204C21.6829 20.058 21.9992 19.2954 22 18.5V10.5C21.9992 9.70459 21.6829 8.94199 21.1204 8.37956C20.558 7.81712 19.7954 7.50079 19 7.5H13V11.5Z" fill="currentColor"/>
                  </svg>
                </button>
                {/* Botão Criar Processo/Caso - dropdown */}
                <div className="relative flex-shrink-0" ref={createBtnDropdownRef}>
                  <button
                    onClick={() => setCreateBtnDropdownOpen((v) => !v)}
                    className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '18px',
                      backgroundColor: '#06141B',
                      color: '#F5F6F5',
                      fontWeight: 400,
                      borderRadius: 10,
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    }}
                    title="Novo processo ou caso"
                  >
                    +
                  </button>
                  {createBtnDropdownOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 z-50 min-w-[160px] py-1 rounded-[10px] border border-gray-200"
                      style={{ backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', fontFamily: 'Poppins, sans-serif' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setCreateBtnDropdownOpen(false);
                          if (!requirePermission('processes.create', 'Criar processos')) return;
                          setCreateModalType('processo');
                          setShowCreateModal(true);
                        }}
                        className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition-colors"
                        style={{ color: '#06141B' }}
                      >
                        Criar processo
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCreateBtnDropdownOpen(false);
                          if (!requirePermission('processes.create', 'Criar casos')) return;
                          setCreateModalType('caso');
                          setShowCreateModal(true);
                        }}
                        className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition-colors"
                        style={{ color: '#06141B' }}
                      >
                        Criar caso
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Filtros externos de processos removidos (filtro agora apenas no dropdown) */}
          </div>

          {/* Conteúdo da lista de processos — altura se adequa ao tamanho da lista */}
          <div
            className="px-6 pt-2 pb-4 flex flex-col"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <div className="flex-1 flex flex-col">
              {!canReadProcesses ? (
                <div className="text-center py-12 flex-1 flex items-center justify-center">
                  <p className="text-[14px] font-semibold text-[#2D3748]" style={{ fontFamily: 'Poppins, sans-serif' }}>Você não tem permissão para ver processos.</p>
                </div>
              ) : isLoadingProcesses ? (
                <div className="text-center py-12 flex-1 flex items-center justify-center">
                  <p className="text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>Carregando processos...</p>
                </div>
              ) : filteredProcesses && filteredProcesses.length > 0 ? (
                <div
                  className="overflow-x-auto"
                  style={{
                    overflowY: 'visible',
                    position: 'relative',
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingBottom: 14,
                  }}
                >
                  {/* Header */}
                  <div
                    className="w-full mb-2"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 20% 17% 7% 7% 6% 7% 13% 5% 5%',
                    }}
                  >
                    <div className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                    </div>
                    <div className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      TÍTULO
                    </div>
                    <div className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      NÚMERO
                    </div>
                    <div className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      STATUS
                    </div>
                    <div className="text-left py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      POLO
                    </div>
                    <div className="text-center py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      TIPO
                    </div>
                    <div className="text-center py-3 px-3 text-xs uppercase tracking-wider flex items-center justify-center" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      RESPONSÁVEL
                    </div>
                    <div className="text-center py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      CLASSE JUDICIAL
                    </div>
                    <div className="text-center py-3 px-3 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      ANDAMENTO
                    </div>
                    {/* Coluna final sem título para o menu de ações */}
                    <div />
                  </div>
                  {/* Lista - cada item é um único container com sombra (mesmo padrão da lista de clientes) */}
                  <div className="w-full flex flex-col" style={{ gap: 14 }}>
                    {filteredProcesses.map((process: ProcessDto) => (
                      <ProcessListItem
                        key={process.id}
                        process={process}
                        workspaceId={workspaceId}
                        clientId={clientId}
                        allMembers={allMembers || []}
                        onEdit={() => {
                          if (!requirePermission('processes.update', 'Editar processos')) return;
                          setEditingProcessId(process.id);
                        }}
                        onArchive={() => {
                          if (!requirePermission('processes.update', 'Arquivar processos')) return;
                          setArchivingProcessId(process.id);
                        }}
                        onShowHistory={() => setShowHistoryModalProcessId(process.id)}
                        onAddTask={() => setShowTaskModalProcessId(process.id)}
                        onClick={() => {
                          navigate({ to: `/w/${workspaceId}/clients/${clientId}/processes/${process.id}/` });
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 flex-1 flex flex-col items-center justify-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <p className="text-[14px] font-semibold text-[#2D3748] mb-1">Nenhum processo ou caso encontrado</p>
                  <p className="text-[12px] text-[#718096] mb-6">Este cliente ainda não possui processos ou casos cadastrados.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (!requirePermission('processes.create', 'Criar processos')) return;
                        setCreateModalType('processo');
                        setShowCreateModal(true);
                      }}
                      className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: '#F9FAFB',
                        color: '#2D3748',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      Criar Primeiro Processo
                    </button>
                    <button
                      onClick={() => {
                        if (!requirePermission('processes.create', 'Criar casos')) return;
                        setCreateModalType('caso');
                        setShowCreateModal(true);
                      }}
                      className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: '#DBEAFE',
                        color: '#1D4ED8',
                        border: '1px solid #BFDBFE',
                      }}
                    >
                      Criar Primeiro Caso
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination - mesmo padrão da lista de clientes */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif', 
                    fontSize: '14px', 
                    fontWeight: 500,
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }}
                >
                  &lt;
                </button>
                <span className="px-4 py-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                  Página {page} de {totalPages} ({total} {total === 1 ? 'item' : 'itens'})
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ 
                    fontFamily: 'Poppins, sans-serif', 
                    fontSize: '14px', 
                    fontWeight: 500,
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Modal de Criar Processo/Caso */}
      {showCreateModal && (
        <ProcessFormModal
          workspaceId={workspaceId}
          clientId={clientId}
          processType={createModalType}
          onClose={() => setShowCreateModal(false)}
          onSave={(data) => {
            if (!requirePermission('processes.create', createModalType === 'caso' ? 'Criar casos' : 'Criar processos')) return;
            createMutation.mutate(data);
          }}
          isSaving={createMutation.isPending}
        />
      )}

      {showEditClientModal && client && (
        <ClientFormModal
          workspaceId={workspaceId}
          clientId={clientId}
          client={client}
          onClose={() => setShowEditClientModal(false)}
          onSave={handleSaveClient}
          isSaving={updateClientMutation.isPending}
        />
      )}

      {showContactsModal && (
        <ClientContactsModal
          workspaceId={workspaceId}
          clientId={clientId}
          onClose={() => setShowContactsModal(false)}
        />
      )}

      {/* Modal de Editar Processo */}
      {editingProcessId && processes.find(p => p.id === editingProcessId) && (
        <ProcessFormModal
          workspaceId={workspaceId}
          clientId={clientId}
          processId={editingProcessId}
          process={processes.find(p => p.id === editingProcessId)}
          onClose={() => setEditingProcessId(null)}
          onSave={(data) => {
            if (!requirePermission('processes.update', 'Editar processos')) return;
            updateProcessMutation.mutate({ processId: editingProcessId, data });
          }}
          isSaving={updateProcessMutation.isPending}
        />
      )}

      {/* Modal de Arquivar Processo */}
      {archivingProcessId && (
        <ArchiveProcessModal
          isOpen={!!archivingProcessId}
          onClose={() => setArchivingProcessId(null)}
          processId={archivingProcessId}
          clientId={clientId}
          workspaceId={workspaceId}
        />
      )}

      {/* Modal de Histórico */}
      {showHistoryModalProcessId && (
        <ProcessHistoryModal
          isOpen={!!showHistoryModalProcessId}
          onClose={() => setShowHistoryModalProcessId(null)}
          workspaceId={workspaceId}
          clientId={clientId}
          processId={showHistoryModalProcessId}
          processType={filteredProcesses.find((p: ProcessDto) => p.id === showHistoryModalProcessId)?.type}
        />
      )}

      {/* Modal de Relatório */}
      {showReportModal && client && (
        <ProcessReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          workspaceId={workspaceId}
          clientId={clientId}
          client={client}
        />
      )}

      {/* Modal de Adicionar Task */}
      {showTaskModalProcessId && (
        <TaskFormModal
          processId={showTaskModalProcessId}
          onClose={() => setShowTaskModalProcessId(null)}
          onSuccess={() => {
            setShowTaskModalProcessId(null);
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId, showTaskModalProcessId] });
          }}
        />
      )}
    </div>
  );
}

// Component for responsible member avatar in process list (tooltip em portal para não ser cortado)
function ProcessResponsibleAvatar({ member }: { member: MemberDto }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getInitials = (name: string | null | undefined): string => {
    if (!name || typeof name !== 'string' || name.length === 0) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    if (!showTooltip || !triggerRef.current) {
      setTooltipPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [showTooltip]);

  const tooltipContent = (
    <div
      className="fixed z-[100001] px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none"
      style={{
        top: tooltipPosition?.top ?? 0,
        left: tooltipPosition?.left ?? 0,
        transform: 'translate(-50%, -100%)',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {member.user?.name}
      <div className="absolute left-1/2 -bottom-1 w-2 h-2 bg-gray-900 transform -translate-x-1/2 rotate-45" />
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className="relative flex-shrink-0"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {member.user?.avatarUrl && member.user.avatarUrl.trim() !== '' && !avatarError ? (
        <>
          <img
            src={member.user.avatarUrl}
            alt={member.user.name}
            className="rounded-full object-cover cursor-pointer transition-all border-2 flex-shrink-0"
            style={{
              width: 25,
              height: 25,
              borderColor: '#F5F6F5',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#F5F6F5';
            }}
            onError={() => setAvatarError(true)}
          />
          {showTooltip && tooltipPosition && createPortal(tooltipContent, document.body)}
        </>
      ) : (
        <>
          <div
            className="rounded-full flex items-center justify-center text-xs font-medium cursor-pointer transition-all border-2 flex-shrink-0"
            style={{
              width: 25,
              height: 25,
              backgroundColor: '#06141B',
              color: '#CCD0CF',
              borderColor: '#F5F6F5',
              fontFamily: "'Poppins', sans-serif",
              textTransform: 'lowercase',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#F5F6F5';
            }}
          >
            {getInitials(member.user?.name).toLowerCase()}
          </div>
          {showTooltip && tooltipPosition && createPortal(tooltipContent, document.body)}
        </>
      )}
    </div>
  );
}

function ProcessListItem({
  process,
  workspaceId,
  clientId,
  allMembers,
  onEdit,
  onArchive,
  onShowHistory,
  onAddTask,
  onClick,
}: {
  process: ProcessDto;
  workspaceId: string;
  clientId: string;
  allMembers: MemberDto[];
  onEdit: () => void;
  onArchive: () => void;
  onShowHistory: () => void;
  onAddTask: () => void;
  onClick: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const DROPDOWN_WIDTH = 192; // w-48

  // Calcular posição do dropdown quando abre (renderizar em portal fora do scroll)
  useEffect(() => {
    if (!menuOpen || !triggerButtonRef.current) {
      setDropdownPosition(null);
      return;
    }
    const rect = triggerButtonRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      left: rect.right - DROPDOWN_WIDTH,
    });
  }, [menuOpen]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (menuRef.current && !menuRef.current.contains(target) && !target.closest('[data-process-row-dropdown]')) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Fechar ao rolar (evita dropdown fixo desalinhado)
  useEffect(() => {
    if (!menuOpen) return;
    const handleScroll = () => setMenuOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpen]);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
    setMenuOpen(false);
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchive();
    setMenuOpen(false);
  };

  const queryClient = useQueryClient();
  const favoriteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/clients/${clientId}/processes/${process.id}/favorite`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId] });
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId, process.id] });
    },
  });

  // Fetch last movement
  const { data: lastMovement } = useQuery({
    queryKey: ['process-last-movement', workspaceId, process.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/clients/${clientId}/processes/${process.id}/sync-history`);
        const movements = response.data.data?.movements || [];
        return movements.length > 0 ? movements[0] : null;
      } catch {
        return null;
      }
    },
    enabled: !!process.id,
  });

  // Fetch last completed task
  const { data: lastCompletedTask } = useQuery<TaskDto | null>({
    queryKey: ['process-last-completed-task', workspaceId, process.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/processes/${process.id}/tasks`, {
          params: { status: 'DONE' },
        });
        const tasks = response.data.data || [];
        const completedTasks = tasks.filter((t: TaskDto) => t.status === 'DONE');
        if (completedTasks.length === 0) return null;
        // Sort by completedAt or updatedAt descending
        completedTasks.sort((a: TaskDto, b: TaskDto) => {
          const aDate = a.completedAt || a.updatedAt;
          const bDate = b.completedAt || b.updatedAt;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
        return completedTasks[0];
      } catch {
        return null;
      }
    },
    enabled: !!process.id,
  });

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const truncateText = (text: string | null | undefined, maxLength: number) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get responsible member - lookup by ID from allMembers (which has user loaded)
  const responsibleMember = useMemo(() => {
    if (!process.responsibleMemberId) return null;
    return allMembers.find((m) => m.id === process.responsibleMemberId) || null;
  }, [process.responsibleMemberId, allMembers]);

  // Get parties name following the same logic as the process header
  const partiesName = useMemo(() => {
    if (!process.parts || process.parts.length === 0) {
      return process.parties || '-';
    }

    const clientPole = process.clientPole || '';
    const activeParts = process.parts.filter((p) => p.pole === 'ativo');
    const passiveParts = process.parts.filter((p) => p.pole === 'passivo');

    const clientActivePart = clientPole === 'ativo' ? activeParts[0] : null;
    const clientPassivePart = clientPole === 'passivo' ? passiveParts[0] : null;

    const otherActiveParts = clientPole === 'ativo' ? activeParts.slice(1) : activeParts;
    const otherPassiveParts = clientPole === 'passivo' ? passiveParts.slice(1) : passiveParts;
    const otherParts = process.parts.filter((p) => !['ativo', 'passivo'].includes(p.pole));

    let partsDisplay = '';

    if (clientPole === 'ativo' && clientActivePart) {
      partsDisplay = clientActivePart.name;

      if (otherActiveParts.length > 0 || passiveParts.length > 0 || otherParts.length > 0) {
        partsDisplay += ' ...';
      }

      if (passiveParts.length > 0 || otherParts.length > 0) {
        partsDisplay += ' vs ';
        if (passiveParts.length > 0) {
          partsDisplay += passiveParts[0].name;
          if (passiveParts.length > 1 || otherParts.length > 0) {
            partsDisplay += ' ...';
          }
        } else if (otherParts.length > 0) {
          partsDisplay += otherParts[0].name;
          if (otherParts.length > 1) {
            partsDisplay += ' ...';
          }
        }
      }
    } else if (clientPole === 'passivo' && clientPassivePart) {
      if (activeParts.length > 0) {
        partsDisplay = activeParts[0].name;
        if (activeParts.length > 1) {
          partsDisplay += ' ...';
        }
      }
      partsDisplay += ' vs ';

      partsDisplay += clientPassivePart.name;

      if (otherPassiveParts.length > 0 || otherParts.length > 0) {
        partsDisplay += ' ...';
      }
    } else {
      if (activeParts.length > 0) {
        partsDisplay = activeParts[0].name;
        if (activeParts.length > 1 || passiveParts.length > 0 || otherParts.length > 0) {
          partsDisplay += ' ...';
        }
      }

      if (passiveParts.length > 0 || otherParts.length > 0) {
        if (partsDisplay) partsDisplay += ' vs ';
        if (passiveParts.length > 0) {
          partsDisplay += passiveParts[0].name;
          if (passiveParts.length > 1 || otherParts.length > 0) {
            partsDisplay += ' ...';
          }
        } else if (otherParts.length > 0) {
          partsDisplay += otherParts[0].name;
          if (otherParts.length > 1) {
            partsDisplay += ' ...';
          }
        }
      }
    }

    return partsDisplay || '-';
  }, [process.parts, process.parties, process.clientPole]);

  const titleText = (process as any).internalTitle?.trim?.() || partiesName;

  // Get status display
  const statusDisplay = process.archivedAt ? 'Arquivado' : (process.status || 'Ativo');

  return (
    <div
      onClick={onClick}
      className="cursor-pointer transition-colors w-full"
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 20% 17% 7% 7% 6% 7% 13% 5% 5%',
        backgroundColor: '#FAFAFA',
        borderRadius: 15,
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
      }}
    >
      {/* Favorito */}
      <div className="py-1 px-3 flex items-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            favoriteMutation.mutate();
          }}
          className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
          title={process.isFavorite ? 'Desfavoritar' : 'Favoritar'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill={process.isFavorite ? '#FBBF24' : 'none'}
            stroke={process.isFavorite ? '#FBBF24' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      </div>

      {/* Título - até duas linhas, centralizado verticalmente */}
      <div
        className="py-1 px-3"
        style={{
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            color: '#2E2D30',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {titleText}
        </span>
      </div>

      {/* Número do processo - centralizado verticalmente */}
      <div className="py-1 px-3 flex items-center">
        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2E2D30' }}>
          {process.processNumber}
        </span>
      </div>

      {/* Status */}
      <div className="py-1 px-3">
        <span
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            color: process.archivedAt ? '#9CA3AF' : '#2E2D30',
            fontWeight: 400,
          }}
        >
          {statusDisplay}
        </span>
      </div>

      {/* Polo */}
      <div className="py-1 px-3">
        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2E2D30' }}>
          {process.clientPole || '-'}
        </span>
      </div>

      {/* Tipo */}
      <div className="py-1 px-3 flex items-center justify-center">
        {(process as any).type === 'caso' ? (
          <span
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '10px',
              fontWeight: 600,
              color: '#1D4ED8',
              backgroundColor: '#DBEAFE',
              borderRadius: 6,
              padding: '2px 7px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Caso
          </span>
        ) : (
          <span
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '10px',
              fontWeight: 600,
              color: '#065F46',
              backgroundColor: '#D1FAE5',
              borderRadius: 6,
              padding: '2px 7px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Processo
          </span>
        )}
      </div>

      {/* Responsável */}
      <div className="py-1 px-3">
        <div className="flex justify-center">
          {responsibleMember && responsibleMember.user ? (
            <ProcessResponsibleAvatar member={responsibleMember as MemberDto} />
          ) : (
            <span className="text-gray-400" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
              -
            </span>
          )}
        </div>
      </div>

      {/* Classe Judicial - centralizado horizontal e verticalmente */}
      <div className="py-1 px-3 flex items-center justify-center">
        <span
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            color: '#2E2D30',
            textAlign: 'center',
          }}
          title={process.judicialClass || undefined}
          className="block truncate max-w-[200px]"
        >
          {truncateText(process.judicialClass, 30)}
        </span>
      </div>

      {/* Andamento */}
      <div className="py-1 px-3 flex items-center justify-center">
        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2E2D30' }}>
          {lastMovement ? formatDate(lastMovement.movementDate) : '-'}
        </span>
      </div>

      {/* Coluna final: menu de ações (três pontinhos) */}
      <div className="py-1 px-3 flex items-center justify-center" style={{ overflow: 'visible', position: 'relative' }}>
        <div className="relative" ref={menuRef}>
          {!process.archivedAt && (
            <>
              <button
                ref={triggerButtonRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                style={{ color: '#A0AEC0' }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="5" r="1.5" fill="currentColor"/>
                  <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                  <circle cx="10" cy="15" r="1.5" fill="currentColor"/>
                </svg>
              </button>
              {menuOpen && dropdownPosition && createPortal(
                <div
                  data-process-row-dropdown
                  className="fixed w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-200 z-[9999]"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                  >
                    {(process as any).type === 'caso' ? 'Editar caso' : 'Editar processo'}
                  </button>
                  <button
                    type="button"
                    onClick={handleArchive}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                  >
                    {(process as any).type === 'caso' ? 'Arquivar caso' : 'Arquivar processo'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddTask();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                  >
                    Adicionar tarefa
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowHistory();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                  >
                    Histórico
                  </button>
                </div>,
                document.body
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
